import { useEffect, useState } from "react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid,
} from "recharts";
import { TrendingUp, Lightbulb, Rocket, Coins, ArrowRight, Clock, MailCheck, Ghost } from "lucide-react";
import { fmtINR, stageMeta, IDEA_STAGES } from "./bizdevUtils";
import FounderBriefDialog from "./FounderBriefDialog";

/** Iter 169 · BizDev Cockpit — weighted speculative revenue visualization. */

function Kpi({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="border border-border bg-card p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-muted-foreground">
        <Icon className="w-3.5 h-3.5" />
        <span className="text-[10px] uppercase tracking-widest">{label}</span>
      </div>
      <div className="text-2xl font-bold mt-2">{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

export default function Cockpit({ wsId, onOpenIdeas, onOpenResurrections }) {
  const [data, setData] = useState(null);
  const [showBrief, setShowBrief] = useState(false);

  useEffect(() => {
    api.get(`/workspaces/${wsId}/bizdev/dashboard`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiErrorMessage(e, "Failed to load cockpit")));
  }, [wsId]);

  if (!data) return <div className="p-8 text-center text-sm text-muted-foreground">Loading cockpit…</div>;

  const stageBars = IDEA_STAGES
    .filter((s) => (data.by_stage[s.key]?.count || 0) > 0)
    .map((s) => ({ name: s.label, count: data.by_stage[s.key].count,
                     upside: data.by_stage[s.key].weighted_monthly }));
  const empty = data.totals.ideas === 0;

  return (
    <div className="space-y-4" data-testid="bizdev-cockpit">
      <div className="flex items-center">
        <div className="text-xs text-muted-foreground">
          Weighted pipeline updates live as you work your ideas.
        </div>
        {data.resurrection?.pending_count > 0 && (
          <button type="button" onClick={onOpenResurrections}
                  className="ml-auto mr-2 inline-flex items-center gap-1.5 text-xs border border-violet-300 bg-violet-50 text-violet-700 px-3 py-1.5 hover:bg-violet-100 transition-colors"
                  data-testid="cockpit-resurrection-cta">
            <Ghost className="w-3.5 h-3.5" />
            {data.resurrection.pending_count} resurrection {data.resurrection.pending_count === 1 ? "candidate" : "candidates"} to review
          </button>
        )}
        <button type="button" onClick={() => setShowBrief(true)}
                className={`${data.resurrection?.pending_count > 0 ? "" : "ml-auto"} inline-flex items-center gap-1.5 text-xs border border-border px-3 py-1.5 hover:border-primary hover:text-primary transition-colors`}
                data-testid="cockpit-founder-brief-btn">
          <MailCheck className="w-3.5 h-3.5" /> Founder Brief
        </button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi icon={TrendingUp} label="Weighted monthly upside"
             value={fmtINR(data.upside.weighted_monthly)}
             sub="likely revenue × confidence, active ideas"
             testid="cockpit-kpi-monthly-upside" />
        <Kpi icon={Coins} label="One-time upside"
             value={fmtINR(data.upside.weighted_one_time)}
             sub="weighted one-off deals" testid="cockpit-kpi-onetime-upside" />
        <Kpi icon={Lightbulb} label="Active ideas"
             value={`${data.totals.active_ideas}`}
             sub={`${data.totals.ideas} total · ${data.totals.launched} launched`}
             testid="cockpit-kpi-active-ideas" />
        <Kpi icon={Rocket} label="Ventures" value={`${data.totals.ventures}`}
             sub="business lines in play" testid="cockpit-kpi-ventures" />
      </div>

      {empty ? (
        <div className="border border-dashed border-border p-12 text-center">
          <Lightbulb className="w-8 h-8 mx-auto text-muted-foreground" />
          <div className="mt-3 text-sm font-medium">Your idea vault is empty</div>
          <div className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">
            Capture every spark — attach ballpark revenue, confidence, and a time horizon,
            and this cockpit turns daydreams into a weighted revenue pipeline.
          </div>
          <button type="button" onClick={onOpenIdeas}
                  className="mt-4 inline-flex items-center gap-1 text-xs text-primary hover:underline"
                  data-testid="cockpit-goto-ideas">
            Add your first idea <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      ) : (
        <>
          <div className="grid lg:grid-cols-2 gap-3">
            <div className="border border-border bg-card p-4">
              <h3 className="text-sm font-semibold mb-1">Speculative revenue onset</h3>
              <p className="text-[11px] text-muted-foreground mb-3">
                Cumulative weighted monthly revenue as active ideas hit their time horizons.
              </p>
              <div className="h-52" data-testid="cockpit-horizon-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data.horizon} margin={{ left: 8, right: 8, top: 4 }}>
                    <defs>
                      <linearGradient id="upsideGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#f97316" stopOpacity={0.5} />
                        <stop offset="100%" stopColor="#f97316" stopOpacity={0.05} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 9 }} tickLine={false} />
                    <YAxis tick={{ fontSize: 9 }} tickLine={false} axisLine={false}
                           tickFormatter={(v) => fmtINR(v)} width={70} />
                    <Tooltip formatter={(v) => fmtINR(v)} labelStyle={{ fontSize: 11 }}
                             contentStyle={{ fontSize: 11 }} />
                    <Area type="stepAfter" dataKey="weighted_monthly" stroke="#f97316"
                          strokeWidth={2} fill="url(#upsideGrad)" name="Weighted monthly" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="border border-border bg-card p-4">
              <h3 className="text-sm font-semibold mb-1">Pipeline by stage</h3>
              <p className="text-[11px] text-muted-foreground mb-3">
                Idea count and weighted upside per stage.
              </p>
              <div className="h-52" data-testid="cockpit-stage-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={stageBars} margin={{ left: 8, right: 8, top: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 9 }} tickLine={false} />
                    <YAxis tick={{ fontSize: 9 }} tickLine={false} axisLine={false}
                           tickFormatter={(v) => fmtINR(v)} width={70} />
                    <Tooltip formatter={(v, n) => n === "Upside/mo" ? fmtINR(v) : v}
                             contentStyle={{ fontSize: 11 }} />
                    <Bar dataKey="upside" fill="#f97316" name="Upside/mo" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-3">
            <div className="border border-border bg-card p-4">
              <h3 className="text-sm font-semibold mb-3">Top ideas by RICE</h3>
              <div className="divide-y" data-testid="cockpit-top-ideas">
                {data.top_ideas.map((i) => (
                  <div key={i.id} className="py-2 flex items-center gap-2 text-xs">
                    <span className={`text-[9px] uppercase tracking-widest border px-1.5 py-0.5 ${stageMeta(i.stage).color}`}>
                      {stageMeta(i.stage).label}
                    </span>
                    <span className="font-medium truncate flex-1">{i.title}</span>
                    {i.venture_name && (
                      <span className="text-muted-foreground truncate max-w-[100px]">{i.venture_name}</span>
                    )}
                    <span className="text-muted-foreground">{fmtINR(i.weighted_monthly)}/mo</span>
                    <span className="font-mono font-bold text-primary">RICE {i.rice_score}</span>
                  </div>
                ))}
                {data.top_ideas.length === 0 && (
                  <div className="py-6 text-center text-xs text-muted-foreground">No active ideas yet.</div>
                )}
              </div>
            </div>
            <div className="border border-border bg-card p-4">
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" /> Next actions — stay honest
              </h3>
              <div className="divide-y" data-testid="cockpit-next-actions">
                {data.next_actions.map((i) => (
                  <div key={i.id} className="py-2 text-xs">
                    <div className="flex items-center gap-2">
                      <span className="font-medium truncate flex-1">{i.title}</span>
                      {i.next_action_due && (
                        <span className={`font-mono text-[10px] ${i.next_action_due < new Date().toISOString().slice(0, 10) ? "text-red-600 font-bold" : "text-muted-foreground"}`}>
                          {i.next_action_due.slice(0, 10)}
                        </span>
                      )}
                    </div>
                    <div className="text-muted-foreground mt-0.5">{i.next_action}</div>
                  </div>
                ))}
                {data.next_actions.length === 0 && (
                  <div className="py-6 text-center text-xs text-muted-foreground">
                    No next actions defined. Every active idea deserves one.
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
      {showBrief && <FounderBriefDialog wsId={wsId} onClose={() => setShowBrief(false)} />}
    </div>
  );
}
